import { createSelector } from 'reselect';
import { isNullOrUndefined } from '@adsk/bim360-matrix-react-components';
import isEmpty from 'lodash/isEmpty';
import lodashGet from 'lodash/get';
import lodashPickBy from 'lodash/pickBy';
import {
  createTransitionStatusOption,
  isChangesetIncludeAnswer,
} from '../../utils/transition-helpers';
import { stringifyQuery } from '../../utils/query-helpers';
import { USER_ROLE_ADMIN, ROLE_STATUS } from '../../constants/roles-types';
import { getSimpleGroupedEntities } from '../../utils/assigned-to-helpers';
import { sortAlphabeticallyByPath } from '../../utils/sortingHelpers';
import { buildActiveUsersArray, parseUserObjectForCommentComponent } from '../../utils/userHelpers';
import { ENV_CONFIGS } from '../../constants/envs';
import { viewTypes } from '../../constants/view-types';
import { ISSUE_FILTER_PROPERTIES } from '../../constants/issue-query-params';
import { STATUS_VOID } from '../../constants/status-types';
import { getChangesetClientAttributes } from '../../utils/changesets-helpers';
import { channelItemTypes } from '../../constants/channel-items';
import { getUserRole } from '../../utils/analytics-helpers';
import { getPermittedStatuses, isAttributePermitted } from '../../utils/issues-helpers';
import { RfiEnums, UNSPECIFIED } from '../../constants/rfi-enums';
import {
  ISSUE_BE_ATTRIBUTE_NAMES,
  ISSUE_ATTRIBUTE_NAMES,
} from '../../constants/issue-attribute-names';
import { ContainerTypes } from '../../constants/container-types';

// composite selectors (that are exported) pass arguments into the following primitive selectors:
// (e.g.: state, and optionally: ownProps)
export const isFFLoaded = (state) => state.ffLoaded;
export const getAccessToken = (state) => state.accessToken;
export const getTargetUrn = (state) => state.targetUrn;
const getConfiguration = (state) => state.configuration;
const getLimit = () => 20;
export const getQueryParams = (state) => state.queryParams;
export const getThumbnails = (state) => state.thumbnails.list;
export const isLoadingThumbnails = (state) => state.thumbnails.isLoading;
export const getNotification = (state) => state.notification;
export const getCurrentUser = (state) => state.currentUser;
export const getCurrentPreviewIssueId = (state) => state.currentPreviewIssueId;
export const isShowNewIssueModal = (state) => state.showNewIssueModal;
export const getActivePushpinAttributes = (state) => state.issuePushpin;
export const getPendingIssue = (state) => state.pendingIssue;
export const getPendingIssueForm = (state) => state.pendingIssueForm.$form;
export const getTransitionFormState = (state) => state.transitionForm.$form;
export const getTransitionFormAttributes = (state) =>
  lodashPickBy(state.transitionForm, (value, key) => {
    return key !== '$form';
  });
export const getPendingIssueFormAttributes = (state) =>
  lodashPickBy(state.pendingIssueForm, (value, key) => {
    return key !== '$form';
  });

export const getPushpinWorkflowEnabled = (state) => state.pushpinWorkflowEnabled;
const getAttachmentUpload = (state) => state.attachmentUpload;
export const getCurrentViewType = (state) => state.currentView;
export const isFetchReportLoading = (state) => state.report.isFetchReportLoading || false;
export const getIsCreatingPdfReport = (state) => state.report.isCreatingPdfReport || false;
export const isLoadingAttachments = (state) => state.attachments.isLoading;
export const getAttachmentsList = (state, includeDeleted = false) =>
  includeDeleted
    ? state.attachments.list
    : state.attachments.list.filter((item) => !item.deletedBy);
export const getCurrentlyViewedAttachments = (state) => state.gallery.attachments;
export const getGallerySelectedIndex = (state) => state.gallery.selectedIndex;
export const isShowPushpinVisibilityPopUp = (state) => state.pushpinVisibilityPopUp.visible;
export const isChangingPushpinVisibility = (state) => state.pushpinVisibilityPopUp.loading;
export const isShowDeletePushpinPopUp = (state) => state.deletePushpinPopUp.visible;
export const isDeletingPushpin = (state) => state.deletePushpinPopUp.loading;
export const getIssuePreview = (state) => state.issuePreview;
export const getIframeUrl = (state) => state.iframeModal.url;
export const getIframeTitle = (state) => state.iframeModal.title;
export const getWorkflow = (state) => (state.workflows.list ? state.workflows.list[0] : {});

export const getOxygenMappings = createSelector(getConfiguration, ({ oxygen }) => {
  if (!oxygen) {
    return null;
  }

  const rolesInProject = {};
  Object.keys(oxygen.roles).map((roleId) => {
    if (oxygen.roles[roleId].isInProject) {
      rolesInProject[roleId] = oxygen.roles[roleId];
    }
  });

  return {
    ...oxygen,
    roles: rolesInProject,
  };
});

export const getAllOxygenMappings = createSelector(getConfiguration, ({ oxygen }) => oxygen);

export const getProjectId = createSelector(getConfiguration, ({ jsonapi }) => jsonapi.projectId);

export const getAnalyticsId = createSelector(
  getConfiguration,
  ({ analyticsConfig }) => analyticsConfig.analyticsId
);

export const getProjectsLength = createSelector(
  getConfiguration,
  ({ analyticsConfig }) => analyticsConfig.projectsLength
);

export const getAnalyticsHostingAppName = createSelector(
  getConfiguration,
  ({ analyticsConfig }) => analyticsConfig.hostingAppName
);

export const getEnv = createSelector(getConfiguration, ({ jsonapi }) => jsonapi.env);

export const getRegion = createSelector(getConfiguration, ({ jsonapi }) => jsonapi.region);

export const getFetchingIssuesUrns = createSelector(
  getConfiguration,
  ({ fetchingIssuesUrns }) => fetchingIssuesUrns
);

export const getApigeeHost = createSelector(
  getEnv,
  getRegion,
  (env, region) => ENV_CONFIGS[region][env].APIGEE_HOST
);

export const getThumbnailUrlFromAttachment = (attachment, thumbnails) => {
  const { urn } = attachment;
  const { [urn]: thumbnailData } = thumbnails;
  return lodashGet(thumbnailData, 'attachmentUrl');
};

export const getThumbnailUrlByAttachment = createSelector(
  getThumbnails,
  (state, attachment) => attachment,
  (thumbnails, attachment) => {
    if (!attachment || !thumbnails) {
      return null;
    }
    return getThumbnailUrlFromAttachment(attachment, thumbnails);
  }
);

export const getPushpinThumbnailData = (changeset) =>
  createSelector(getThumbnails, isLoadingThumbnails, (thumbnails, isLoadingThumbnails) => {
    const isThumbnailsFetched = !isLoadingThumbnails && !(Object.keys(thumbnails).length === 0);
    const removedPushPinUrn = lodashGet(changeset, 'changes.linkedDocument[0]');
    const addedPushPinUrn = lodashGet(changeset, 'changes.linkedDocument[1]');

    let changesetDocumentThumbnail;
    let changesetDocumentName = ' ';

    if (isThumbnailsFetched) {
      if (addedPushPinUrn) {
        changesetDocumentThumbnail = thumbnails[addedPushPinUrn];
        changesetDocumentName = changesetDocumentThumbnail && changesetDocumentThumbnail.name;
      }

      if (removedPushPinUrn) {
        changesetDocumentThumbnail = thumbnails[removedPushPinUrn];
        changesetDocumentName = changesetDocumentThumbnail && changesetDocumentThumbnail.name;
      }
    }

    return {
      changesetDocumentThumbnail,
      changesetDocumentName,
    };
  });

export const getCurrentlyViewedAttachmentsWithThumbnails = createSelector(
  getCurrentlyViewedAttachments,
  getThumbnails,
  (attachments, thumbnails) => {
    if (!attachments) {
      return null;
    }
    return attachments.map((attachment) => {
      if (!attachment || !thumbnails) {
        return null;
      }
      const thumbnailUrl = getThumbnailUrlFromAttachment(attachment, thumbnails);
      return { name: attachment.name, original: thumbnailUrl, thumbnail: thumbnailUrl };
    });
  }
);

export const getGalleryIsOpen = createSelector(
  getCurrentlyViewedAttachmentsWithThumbnails,
  (attachments) => {
    return !!attachments && !!attachments.length;
  }
);

// "current" document should be same as one for given container-id/project-id.
// TODO: Refactor so current-document is retrieved solely from document-collection in Redux state:
export const getCurrentDocument = createSelector(getConfiguration, ({ document }) => document);

export const getDocumentCollection = createSelector(
  getConfiguration,
  getTargetUrn,
  getCurrentDocument,
  ({ documentCollection }, targetUrn, currentDocument) => {
    if (targetUrn && !documentCollection[targetUrn]) {
      // Add current document in case it isn't there already. This can happen when current document doesn't have any issues yet.
      const fixedDocumentCollection = { ...documentCollection };
      fixedDocumentCollection[targetUrn] = currentDocument;
      return fixedDocumentCollection;
    }
    return documentCollection;
  }
);

export const isDocumentSelectionOpen = createSelector(
  getAttachmentUpload,
  (attachmentUpload) => attachmentUpload.isDocumentSelectionOpen
);

export const isUploadingAttachments = createSelector(
  getAttachmentUpload,
  (state, origin) => origin,
  (attachmentUpload, origin) => {
    const uploadingAttachmentsArray = attachmentUpload.uploadingAttachments
      ? attachmentUpload.uploadingAttachments
      : [];
    return !!uploadingAttachmentsArray.find((obj) => obj.origin === origin && obj.isSaving);
  }
);

export const getUploadingAttachments = createSelector(
  getAttachmentUpload,
  getCurrentPreviewIssueId,
  (attachmentUpload, issuesId) => {
    const uploadingAttachments = attachmentUpload.uploadingAttachments
      ? attachmentUpload.uploadingAttachments.filter(
          (attachment) => attachment.issueId === issuesId
        )
      : [];
    return uploadingAttachments;
  }
);

const getCurrentDocumentVersion = createSelector(getCurrentDocument, (document) =>
  document ? document.version : null
);

export const getDocumentName = (documentCollection, targetUrn) => {
  const document = documentCollection[targetUrn];
  if (!document) {
    return '';
  }
  return document.name;
};

export const isDocumentUnavailable = (documentCollection, targetUrn) => {
  // For unavailable document, the documentCollection[targetUrn] is an empty object.
  return (
    isEmpty(documentCollection) ||
    (documentCollection[targetUrn] &&
      (Object.keys(documentCollection[targetUrn]).length === 0 ||
        !isNullOrUndefined(documentCollection[targetUrn].error)))
  );
};

export const getDocumentVersion = (documentCollection, targetUrn) => {
  const document = documentCollection[targetUrn];
  if (!document) {
    return null;
  }
  return document.version;
};

export const isLoadingCurrentUser = createSelector(getCurrentUser, ({ loading }) => loading);

// getFetchParams passes state into primitive selectors:
export const getFetchParams = createSelector(
  getLimit,
  getTargetUrn,
  getQueryParams,
  getCurrentDocumentVersion,
  (limit, targetUrn, queryParams, documentVersion) => {
    const { offset } = queryParams;
    return { offset, limit, targetUrn, queryParams, documentVersion };
  }
);

export const getQueryKey = createSelector(getFetchParams, (params) => {
  // params is a nested object and stringifyQuery does not support nested objects (which is a good behavior).
  // Therefore, we're "flattening" the object by extracting queryParams, stringifying both objects and concating them.
  const filterParams = { ...params.queryParams };
  const otherParams = { ...params };
  delete otherParams.queryParams;
  return `${stringifyQuery(filterParams)}&${stringifyQuery(otherParams)}`;
});

export const getLastQueryKey = (state) => state.issues.lastQueryKey;

export const getQueryKeyIsCurrent = createSelector(
  getQueryKey,
  (state, type, queryKey) => queryKey, // pass-through 3rd argument from higher-order/composite selector
  (currentQueryKey, queryKey) => currentQueryKey === queryKey
);

export const getCurrentUserInMcFormat = createSelector(
  getCurrentUser,
  getOxygenMappings,
  (currentUser, oxygen) =>
    lodashGet(currentUser, 'attributes') ? oxygen.users[currentUser.attributes.id] || {} : {}
);

export const isAdmin = createSelector(
  getCurrentUser,
  (user) => lodashGet(user, 'attributes.role') === USER_ROLE_ADMIN
);

export const getDocsCurrentProject = createSelector(
  (state) => state.currentProject,
  ({ docsCurrentProject }) => docsCurrentProject
);

export const getCurrentProject = createSelector(
  (state) => state.currentProject,
  ({ currentProject }) => currentProject
);

export const getCurrentProjectScope = createSelector(
  getCurrentProject,
  ({ projectScope }) => projectScope
);

export const getProjectName = createSelector(getCurrentProject, ({ name }) => name || '');

export const isCostEnabled = createSelector(getCurrentProject, ({ containers, services }) => {
  return (
    containers &&
    services &&
    containers.find(({ type }) => type === ContainerTypes.COST) &&
    services.find(({ type }) => type === ContainerTypes.COST)
  );
});

export const isMeetingEnabled = createSelector(getCurrentProject, ({ containers }) => {
  return containers && containers.find(({ type }) => type === ContainerTypes.MEETING);
});

const getIssuesCollectionWrapper = (state) => state.issues;

export const getIssuesCollectionError = createSelector(
  getIssuesCollectionWrapper,
  ({ error }) => error
);

export const getIssuesCollection = createSelector(
  getIssuesCollectionWrapper,
  (issues) => issues.collection
);

export const getIssuesCollectionPagiantion = createSelector(
  getIssuesCollectionWrapper,
  ({ pagination }) => pagination
);

export const getIssuesCollectionSearchResults = createSelector(
  getIssuesCollectionWrapper,
  ({ searchResults }) => searchResults
);

export const getIssuesOrder = createSelector(
  getIssuesCollectionWrapper,
  ({ sortOrder, collection }) =>
    sortOrder.filter((id) => collection[id] && collection[id].attributes)
);

const EMPTY_OBJECT = Object.freeze({});

export const getCurrentPreviewIssueWrapper = createSelector(
  getIssuesCollection,
  getCurrentPreviewIssueId,
  (collection, issueId) => collection[issueId] || EMPTY_OBJECT
);

export const getCurrentPreviewIssueAttributes = createSelector(
  getCurrentPreviewIssueWrapper,
  ({ attributes }) => {
    return attributes || EMPTY_OBJECT;
  }
);

export const getIsUserCanEditRfiDocument = createSelector(
  getCurrentPreviewIssueAttributes,
  (issue) => {
    const isUserCanEditRfiDocument = isAttributePermitted(
      issue,
      ISSUE_BE_ATTRIBUTE_NAMES[ISSUE_ATTRIBUTE_NAMES.LINKED_DOCUMENT]
    );
    return isUserCanEditRfiDocument;
  }
);

export const getCurrentPreviewIssueChannelItemsWrapper = createSelector(
  getCurrentPreviewIssueWrapper,
  ({ channelItems }) => channelItems || EMPTY_OBJECT
);

export const getCurrentPreviewIssueSortedChannelItems = createSelector(
  getCurrentPreviewIssueChannelItemsWrapper,
  (channelItemsWrapper) => {
    if (channelItemsWrapper.sortOrder) {
      const items = channelItemsWrapper.sortOrder
        .map((id) => channelItemsWrapper.collection[id])
        .reduce((result, item) => {
          if (item) {
            if (item.type === channelItemTypes.CHANGESETS) {
              const attributes = getChangesetClientAttributes(item);
              if (!isEmpty(attributes)) {
                item.changesetClientAttributes = attributes;
                result.push(item);
              }
            } else {
              result.push(item);
            }
          }
          return result;
        }, []);
      // remove this code once BE will match the created_at with answered_at
      const lastOfficialResponse = items.filter((item) => isChangesetIncludeAnswer(item)).pop();
      return items.map((item) => {
        if (isChangesetIncludeAnswer(item) && item.createdAt === lastOfficialResponse.createdAt) {
          item.isLastOfficialResponse = true;
        }
        return item;
      });
    }
    return [];
  }
);

export const getAllIssues = createSelector(getIssuesCollectionWrapper, ({ collection }) => {
  return Object.keys(collection)
    .map((id) => {
      const { error, attributes } = collection[id];
      return !error && attributes;
    })
    .filter((attributes) => attributes);
});

export const getCurrentSheets = createSelector(
  getConfiguration,
  ({ sheetsConfig }) => sheetsConfig.currentSheets
);

export const getAllIssueWrappersSorted = createSelector(
  getIssuesCollectionWrapper,
  ({ sortOrder, collection }) => {
    return sortOrder.map((id) => collection[id]).filter((issue) => !!issue);
  }
);

export const getIssueWrappersSorted = createSelector(getAllIssueWrappersSorted, (allIssues) => {
  return allIssues.filter((issue) => issue.attributes);
});

export const getRFIs = createSelector(getIssuesCollectionWrapper, ({ collection }) => {
  const result = [];

  // eslint-disable-next-line no-restricted-syntax
  for (const issueId in collection) {
    result.push({ id: issueId, ...collection[issueId].attributes });
  }

  return result;
});

export const getSortedIssuesFilteredBySheetsInDocument = createSelector(
  getConfiguration,
  getIssueWrappersSorted,
  ({ sheetsConfig }, issues) => {
    // Here it filters out the issues not in currentSheet, nor in sheetList
    return issues.filter((issue) => {
      return (
        !issue.attributes.sheetMetadata ||
        // It seems that no currentSheet means: Show all
        !sheetsConfig.currentSheets.length ||
        sheetsConfig.sheetList.find(
          (sheet) =>
            sheet.name === issue.attributes.sheetMetadata.sheetName ||
            sheet.guid === issue.attributes.sheetMetadata.sheetGuid
        )
      );
    });
  }
);

export const getReportsSdkConfig = createSelector(
  getEnv,
  getRegion,
  getAccessToken,
  (env, region, { token }) => {
    const config = ENV_CONFIGS[region][env].REPORT_SDK_CONFIG;

    return {
      ...config,
      getAccessToken: () => Promise.resolve(token),
    };
  }
);

export const getMatrixConfig = createSelector(
  getEnv,
  getRegion,
  (env, region) => ENV_CONFIGS[region][env].MATRIX_CONFIG
);

export const getReportContainerId = createSelector(
  getConfiguration,
  ({ jsonapi }) => jsonapi.reportContainerId
);

export const getCurrentIssuePreviewStatusOptions = createSelector(
  getCurrentPreviewIssueAttributes,
  (issue) => {
    const options = [];
    if (isEmpty(issue)) {
      return options;
    }
    const currentStatus = issue.status;

    options.push(createTransitionStatusOption({ status: currentStatus }));

    const voidTransition = getPermittedStatuses(issue).find(
      (transition) => transition.status === STATUS_VOID
    );
    if (voidTransition) {
      options.push(createTransitionStatusOption(voidTransition));
    }

    return options;
  }
);

export const getActiveUsers = createSelector(getOxygenMappings, (oxygen) => {
  return sortAlphabeticallyByPath(buildActiveUsersArray(oxygen.users), 'name');
});

export const getProjectRoles = createSelector(getOxygenMappings, (oxygen) => {
  return sortAlphabeticallyByPath(Object.values(oxygen.roles), 'name');
});

export const getActiveRoles = createSelector(getAllOxygenMappings, (oxygen) => {
  const activeRoles = {};
  Object.keys(oxygen.roles).map((roleId) => {
    if (oxygen.roles[roleId].status === ROLE_STATUS.ACTIVE) {
      activeRoles[roleId] = oxygen.roles[roleId];
    }
  });
  return activeRoles;
});

export const getCurrentProjectActiveRolesInUse = createSelector(
  getActiveRoles,
  getActiveUsers,
  (roles, users) => {
    const usedRoles = {};
    users.forEach((user) =>
      user.roles.forEach((role) => {
        if (roles[role.id]) {
          usedRoles[role.id] = roles[role.id];
        }
      })
    );
    return sortAlphabeticallyByPath(Object.values(usedRoles), 'name');
  }
);

export const getInactiveRoles = createSelector(getAllOxygenMappings, (oxygen) => {
  const inactiveRoles = {};
  Object.keys(oxygen.roles).map((roleId) => {
    if (oxygen.roles[roleId].status === ROLE_STATUS.INACTIVE) {
      inactiveRoles[roleId] = oxygen.roles[roleId];
    }
  });
  return inactiveRoles;
});

export const getProjectCompanies = createSelector(getOxygenMappings, (oxygen) => {
  return sortAlphabeticallyByPath(Object.values(oxygen.companies), 'name');
});

export const getAllProjectEntities = createSelector(getAllOxygenMappings, (oxygen) =>
  Object.values(oxygen.users)
    .concat(Object.values(oxygen.roles))
    .concat(Object.values(oxygen.companies))
);

export const getGroupedProjectActiveEntities = createSelector(
  getActiveUsers,
  getProjectCompanies,
  getProjectRoles,
  (users, companies, roles) => getSimpleGroupedEntities(users, companies, roles)
);

export const getGroupedProjectEntitiesWithActiveRolesInUse = createSelector(
  getActiveUsers,
  getProjectCompanies,
  getCurrentProjectActiveRolesInUse,
  (users, companies, roles) => getSimpleGroupedEntities(users, companies, roles)
);

export const getFlatGroupedProjectEntities = createSelector(
  getGroupedProjectActiveEntities,
  (projectEntities) => {
    // Create an array of object of all the users(projectEntities[0])/rolls(projectEntities[1])/companies(projectEntities[2])
    return projectEntities[0][1].concat(projectEntities[1][1]).concat(projectEntities[2][1]);
  }
);

export const getCurrentProjectEntitiesWithActiveRoles = createSelector(
  getAllOxygenMappings,
  getActiveRoles,
  (oxygen, activeRoles) => {
    return {
      ...oxygen,
      roles: activeRoles,
    };
  }
);

export const getGroupedProjectActiveEntitiesForMentionComponent = createSelector(
  getGroupedProjectActiveEntities,
  (projectEntities) => {
    if (!projectEntities.length) {
      return projectEntities;
    }

    // getGroupedProjectActiveEntities builds a 3 cells array - [users, companies, roles].
    // Each array cell is an array which has 2 cells [title, objectsArray].
    // We only want to manipulate the users array and keep companies and roles as is.
    const users = projectEntities[0].slice();
    const usersForMentionComponent = users[1].map((user) =>
      parseUserObjectForCommentComponent(user)
    );
    users[1] = usersForMentionComponent;

    return [users].concat([projectEntities[1]]).concat([projectEntities[2]]);
  }
);

export const getGroupedProjectActiveEntitiesForMentionComponentWithRolesInUse = createSelector(
  getGroupedProjectEntitiesWithActiveRolesInUse,
  (projectEntities) => {
    if (!projectEntities.length) {
      return projectEntities;
    }

    // getGroupedProjectActiveEntities builds a 3 cells array - [users, companies, roles].
    // Each array cell is an array which has 2 cells [title, objectsArray].
    // We only want to manipulate the users array and keep companies and roles as is.
    const users = projectEntities[0].slice();
    const usersForMentionComponent = users[1].map((user) =>
      parseUserObjectForCommentComponent(user)
    );
    users[1] = usersForMentionComponent;

    return [users].concat([projectEntities[1]]).concat([projectEntities[2]]);
  }
);

export const isShowingHiddenRFIs = createSelector(
  getQueryParams,
  (queryParams) => queryParams[ISSUE_FILTER_PROPERTIES.INCLUDE_HIDDEN_FILTER]
);

export const isVoidedFilterOn = createSelector(getQueryParams, (queryParams) =>
  queryParams[ISSUE_FILTER_PROPERTIES.STATUS_FILTER].includes(STATUS_VOID)
);

export const getDerivativeServiceHost = createSelector(
  getEnv,
  getRegion,
  (env, region) => ENV_CONFIGS[region][env].DERIVATIVE_SERVICE
);

export const isDocumentViewLoading = createSelector(
  getTargetUrn,
  getCurrentDocument,
  (targetUrn, document) => targetUrn === null || document === null
);

export const isMandatoryAppDataLoading = createSelector(
  getOxygenMappings,
  getConfiguration,
  (oxygen, { jsonapi }) => !jsonapi || !oxygen
);

// Placing this last to verify all selectors have been initialized
export const getAppLoading = createSelector(
  isMandatoryAppDataLoading,
  isDocumentViewLoading,
  getCurrentViewType,
  (isMandatoryAppDataLoading, isLoadingDocumentView, viewType) =>
    isMandatoryAppDataLoading || (viewType === viewTypes.DOCUMENT && isLoadingDocumentView)
);

export const getIssuesCollectionLoading = createSelector(
  getIssuesCollectionWrapper,
  ({ batchLoading }) => batchLoading || false
);

export const isIssuePreviewTransitionPopupOpen = createSelector(
  getIssuePreview,
  (issuePreview) => issuePreview.isTransitionPopupOpen
);

export const isIssuePreviewModalNavigationDisabled = createSelector(
  getIssuesCollectionLoading,
  isIssuePreviewTransitionPopupOpen,
  (isCollectionLoading, isTransitionPopupOpen) => isCollectionLoading || isTransitionPopupOpen
);

export const currentUserRoleForAnalytics = createSelector(getCurrentUser, (currentUser) => {
  const currentUserWorkflowRoles = lodashGet(currentUser, 'attributes.rfis.workflowRoles');
  const isProjectAdmin = lodashGet(currentUser, 'attributes.isProjectAdmin');
  return getUserRole(currentUserWorkflowRoles, isProjectAdmin);
});

export const hasPermissionsViolation = (state) => state.permissions.hasViolation;
export const getPermissionsHasGrowled = (state) => state.permissions.hasGrowled;
export const getIsPackageDeepLinking = (state) => state.deepLinking.package;
export const getIsCreateFormDeepLinking = (state) => state.deepLinking.createForm;
export const isFetchingRfiCustomIdentifier = (state) => state.rfiCustomIdentifier.isFetching;
export const isValidatingRfiCustomIdentifier = (state) => state.rfiCustomIdentifier.isValidating;
export const getIsRfiCustomIdentifierValid = (state) => state.rfiCustomIdentifier.isValid;
export const getSuggestedNextRfiCustomIdentifier = (state) =>
  state.rfiCustomIdentifier.suggestedNextCustomIdentifier;
export const getShowValidationErrorMessage = (state) =>
  state.rfiCustomIdentifier.showValidationErrorMessage;
export const getShowValidationErrorMessageLink = (state) =>
  state.rfiCustomIdentifier.showValidationErrorMessageLink;
export const rfiCustomIdentifierAnalyticsData = (state) => state.rfiCustomIdentifier.analyticsData;
export const getRfiCustomIdentifierAnalyticsDataIsEdited = (state) =>
  state.rfiCustomIdentifier.analyticsData.isEdited;
export const rfiSpecSection = (state) => state.specSections;

export const getImpacts = createSelector(() => RfiEnums.costImpact);

export const getRFIEnum = (enumName, withUnspecified) => {
  return createSelector(() => {
    const arr = RfiEnums[enumName] || [];
    return withUnspecified ? [...arr, UNSPECIFIED] : arr;
  });
};
